// Calculadora de ROI interactiva. Inputs: ventas mensuales (Bs), vendedores,
// margen actual %. Outputs: margen recuperado, horas/mes ahorradas, ROI estimado.
// Heurísticas: +8% margen sobre las ventas, 15h ahorradas/vendedor/mes, costo
// Prixia estimado ~0.5% de las ventas (con piso de Bs 800/mes).

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Calculator, TrendingUp, Clock, Sparkles, CalendarCheck } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { useBookDemoStore } from '../store/book-demo-store';

function fmtBs(n: number): string {
  return new Intl.NumberFormat('es-BO', { maximumFractionDigits: 0 }).format(Math.round(n));
}

export function RoiCalculatorSection() {
  const [ventas, setVentas] = useState<number>(500_000);
  const [vendedores, setVendedores] = useState<number>(8);
  const [margen, setMargen] = useState<number>(22);
  const openDemo = useBookDemoStore((s) => s.open);

  const { marginGain, hoursSaved, monthlyCost, roi, annualGain } = useMemo(() => {
    const v = Math.max(0, Number.isFinite(ventas) ? ventas : 0);
    const s = Math.max(1, Number.isFinite(vendedores) ? vendedores : 1);
    const marginGain = v * 0.08;
    const hoursSaved = s * 15;
    const monthlyCost = Math.max(800, v * 0.005);
    const roi = monthlyCost > 0 ? marginGain / monthlyCost : 0;
    const annualGain = marginGain * 12;
    return { marginGain, hoursSaved, monthlyCost, roi, annualGain };
  }, [ventas, vendedores]);

  return (
    <section id="roi" className="relative py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-500/10 border border-violet-200 dark:border-violet-500/20 text-violet-700 dark:text-violet-400 text-xs font-semibold mb-4">
            <Calculator className="w-3.5 h-3.5" />
            Calculá tu ROI
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            ¿Cuánta plata te{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-amber-600">
              devuelve Prixia
            </span>
            ?
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Ajustá los números a tu empresa y mirá el impacto estimado en margen, horas y ROI.
          </p>
        </ScrollReveal>

        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-8">
            {/* Inputs */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 dark:backdrop-blur-sm p-6 lg:p-8">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-6">
                Tu empresa hoy
              </h3>
              <div className="space-y-6">
                <RoiInput
                  label="Ventas mensuales (Bs)"
                  value={ventas}
                  onChange={setVentas}
                  min={50000}
                  max={20_000_000}
                  step={50000}
                  formatter={fmtBs}
                  prefix="Bs"
                />
                <RoiInput
                  label="Vendedores en el equipo"
                  value={vendedores}
                  onChange={setVendedores}
                  min={1}
                  max={200}
                  step={1}
                  formatter={(n) => n.toString()}
                />
                <RoiInput
                  label="Margen actual (%)"
                  value={margen}
                  onChange={setMargen}
                  min={5}
                  max={60}
                  step={1}
                  formatter={(n) => n.toString()}
                  suffix="%"
                />
              </div>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-6 leading-relaxed">
                Estimación basada en métricas promedio de clientes Prixia: +8% margen recuperado, 15
                h/mes ahorradas por vendedor.
              </p>
            </div>

            {/* Outputs */}
            <div className="rounded-2xl border border-violet-200 dark:border-violet-500/30 bg-gradient-to-br from-violet-50 via-white to-amber-50/50 dark:from-violet-950/40 dark:via-gray-900 dark:to-amber-950/30 p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-violet-300/20 dark:bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-violet-700 dark:text-violet-400 mb-6 relative">
                Lo que ganás con Prixia
              </h3>
              <div className="space-y-5 relative">
                <RoiOutput
                  icon={TrendingUp}
                  label="Margen recuperado / mes"
                  value={`Bs ${fmtBs(marginGain)}`}
                  sub={`Bs ${fmtBs(annualGain)} al año`}
                  highlight
                />
                <RoiOutput
                  icon={Clock}
                  label="Horas ahorradas / mes"
                  value={`${fmtBs(hoursSaved)} h`}
                  sub={`Equivale a ${(hoursSaved / 160).toFixed(1)} jornadas completas`}
                />
                <RoiOutput
                  icon={Sparkles}
                  label="ROI estimado"
                  value={`${roi >= 10 ? Math.round(roi) : roi.toFixed(1)}x`}
                  sub={`Costo Prixia estimado: Bs ${fmtBs(monthlyCost)}/mes`}
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() =>
                  openDemo({
                    message: `Ventas mensuales: Bs ${fmtBs(ventas)} · ${vendedores} vendedores · margen ${margen}%. Quiero validar el ROI estimado de Bs ${fmtBs(marginGain)}/mes.`,
                  })
                }
                className="mt-7 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-amber-600 text-white font-semibold text-sm shadow-lg shadow-violet-600/25 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all"
              >
                <CalendarCheck className="w-4 h-4" />
                Validar este ROI en una demo
              </motion.button>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ── Subcomponents ───────────────────────────────────────────────

function RoiInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatter,
  prefix,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step: number;
  formatter: (n: number) => string;
  prefix?: string;
  suffix?: string;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
          {label}
        </label>
        <div className="flex items-center gap-1 px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-sm font-mono font-bold text-gray-900 dark:text-white">
          {prefix && <span className="text-gray-400 text-xs">{prefix}</span>}
          <input
            type="text"
            inputMode="numeric"
            value={text}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d]/g, '');
              setText(raw);
              const n = parseInt(raw || '0', 10);
              if (!isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
            }}
            className="w-20 bg-transparent text-right focus:outline-none"
          />
          {suffix && <span className="text-gray-400 text-xs">{suffix}</span>}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-gradient-to-br
          [&::-webkit-slider-thumb]:from-violet-600
          [&::-webkit-slider-thumb]:to-blue-600
          [&::-webkit-slider-thumb]:shadow-md
          [&::-webkit-slider-thumb]:cursor-grab
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-violet-600
          [&::-moz-range-thumb]:border-0
        "
      />
      <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-mono">
        <span>{formatter(min)}</span>
        <span>{formatter(max)}</span>
      </div>
    </div>
  );
}

function RoiOutput({
  icon: Icon,
  label,
  value,
  sub,
  highlight,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          highlight
            ? 'bg-gradient-to-br from-violet-600 to-amber-600 text-white shadow-lg shadow-violet-600/30'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </p>
        <p
          className={`font-bold leading-none ${
            highlight
              ? 'text-3xl sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-violet-700 to-amber-700 dark:from-violet-300 dark:to-amber-300'
              : 'text-2xl text-gray-900 dark:text-white'
          }`}
        >
          {value}
        </p>
        {sub && <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
  );
}
